import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, KeyRound, CheckCircle2, XCircle, RefreshCw, Trash2, ExternalLink, Sparkles, Cpu } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AISettings() {
  const [providers, setProviders] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // { providerId: boolean }
  const [keyInputs, setKeyInputs] = useState({}); // { providerId: string }
  const [showKey, setShowKey] = useState({}); // { providerId: boolean } — mostrar input

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/ai/providers`),
        axios.get(`${API}/admin/ai/active`),
      ]);
      setProviders(pRes.data.providers || []);
      setActive(aRes.data || null);
    } catch (e) {
      toast.error('Erro ao carregar configurações de IA');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveKey = async (pid) => {
    const key = (keyInputs[pid] || '').trim();
    if (!key) { toast.error('Cole a API key antes de salvar'); return; }
    setBusy(b => ({ ...b, [pid]: true }));
    try {
      const res = await axios.post(`${API}/admin/ai/providers/${pid}/key`, { api_key: key });
      toast.success(`Key salva. ${res.data.count} modelo(s) disponível(eis).`);
      setKeyInputs(k => ({ ...k, [pid]: '' }));
      setShowKey(s => ({ ...s, [pid]: false }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao validar a API key');
    }
    setBusy(b => ({ ...b, [pid]: false }));
  };

  const refresh = async (pid) => {
    setBusy(b => ({ ...b, [pid]: true }));
    try {
      const res = await axios.post(`${API}/admin/ai/providers/${pid}/refresh`);
      toast.success(`Atualizado: ${res.data.count} modelo(s)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao atualizar modelos');
    }
    setBusy(b => ({ ...b, [pid]: false }));
  };

  const removeKey = async (pid) => {
    if (!window.confirm('Remover a API key deste provedor?')) return;
    setBusy(b => ({ ...b, [pid]: true }));
    try {
      await axios.delete(`${API}/admin/ai/providers/${pid}/key`);
      toast.success('Key removida');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao remover');
    }
    setBusy(b => ({ ...b, [pid]: false }));
  };

  const activate = async (provider, model_id) => {
    try {
      await axios.post(`${API}/admin/ai/active`, { provider, model_id });
      toast.success('Modelo ativo definido');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao ativar modelo');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ai-settings-page">
      <div className="mb-8">
        <h1 className="text-3xl font-black text-gray-900 dark:text-white flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-500" /> Configurações de IA
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Cadastre suas API keys de provedores de IA. Ao salvar, o sistema busca automaticamente
          todos os modelos disponíveis nessa conta e você escolhe qual será usado.
        </p>
      </div>

      {/* Modelo ativo */}
      <div className="mb-8 p-5 rounded-2xl border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20">
        <div className="flex items-center gap-3 mb-2">
          <Cpu className="w-5 h-5 text-purple-600" />
          <h2 className="font-bold text-lg">Modelo ativo do sistema</h2>
        </div>
        {active?.provider && active?.model_id ? (
          <div className="flex items-center gap-3" data-testid="ai-active-display">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white dark:bg-gray-800 border border-purple-300 text-sm font-semibold">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span className="capitalize">{active.provider}</span>
              <span className="text-gray-400">/</span>
              <code className="text-purple-700 dark:text-purple-300">{active.model_id}</code>
            </span>
          </div>
        ) : (
          <p className="text-sm text-gray-500" data-testid="ai-active-empty">
            Nenhum modelo ativo. Cadastre uma API key abaixo e clique em "Ativar" no modelo desejado.
          </p>
        )}
      </div>

      {/* Cards de provedores */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {providers.map(p => {
          const isActive = active?.provider === p.id;
          return (
            <div
              key={p.id}
              className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden shadow-sm hover:shadow-md transition"
              data-testid={`ai-provider-${p.id}`}
              style={{ borderTop: `4px solid ${p.brand_color}` }}
            >
              {/* Header */}
              <div className="p-4 flex items-center gap-3 border-b border-gray-100 dark:border-gray-700">
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
                  <img src={p.logo} alt={p.name} className="w-8 h-8 object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 dark:text-white">{p.name}</h3>
                  {p.configured ? (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Configurado — <code className="ml-1">{p.api_key_masked}</code>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 flex items-center gap-1">
                      <XCircle className="w-3 h-3" /> Sem API key
                    </p>
                  )}
                </div>
              </div>

              {/* Corpo: input de key ou lista de modelos */}
              <div className="p-4 space-y-3">
                {!p.configured || showKey[p.id] ? (
                  <div>
                    <label className="text-xs font-semibold text-gray-600 dark:text-gray-300 flex items-center gap-1 mb-1">
                      <KeyRound className="w-3.5 h-3.5" /> API Key
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="password"
                        value={keyInputs[p.id] || ''}
                        onChange={(e) => setKeyInputs(k => ({ ...k, [p.id]: e.target.value }))}
                        placeholder={`Cole sua ${p.name} API key`}
                        className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid={`ai-key-input-${p.id}`}
                      />
                      <button
                        onClick={() => saveKey(p.id)}
                        disabled={busy[p.id]}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-50 flex items-center gap-1"
                        data-testid={`ai-key-save-${p.id}`}
                      >
                        {busy[p.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salvar'}
                      </button>
                    </div>
                    <a
                      href={p.help.split('em ')[1]}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1"
                    >
                      Onde pegar a key <ExternalLink className="w-3 h-3" />
                    </a>
                    {p.configured && (
                      <button onClick={() => setShowKey(s => ({ ...s, [p.id]: false }))} className="text-xs text-gray-400 hover:underline mt-1 ml-3">
                        Cancelar
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                          Modelos disponíveis ({p.models.length})
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => refresh(p.id)}
                            disabled={busy[p.id]}
                            className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded disabled:opacity-50 flex items-center gap-1"
                            title="Buscar modelos novamente"
                            data-testid={`ai-refresh-${p.id}`}
                          >
                            {busy[p.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Atualizar
                          </button>
                          <button
                            onClick={() => setShowKey(s => ({ ...s, [p.id]: true }))}
                            className="text-xs px-2 py-1 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Trocar API key"
                            data-testid={`ai-change-key-${p.id}`}
                          >
                            Trocar key
                          </button>
                          <button
                            onClick={() => removeKey(p.id)}
                            disabled={busy[p.id]}
                            className="text-xs p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                            title="Remover key"
                            data-testid={`ai-remove-${p.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                      {p.models.length === 0 ? (
                        <p className="text-xs text-gray-400 italic">Nenhum modelo listado. Clique em Atualizar.</p>
                      ) : (
                        <div className="max-h-56 overflow-y-auto space-y-1 pr-1" data-testid={`ai-models-list-${p.id}`}>
                          {p.models.map(m => {
                            const isThisActive = isActive && active?.model_id === m.id;
                            return (
                              <div
                                key={m.id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs ${
                                  isThisActive
                                    ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                    : 'border-gray-100 dark:border-gray-700 hover:border-blue-300'
                                }`}
                              >
                                <code className="flex-1 truncate font-mono text-gray-700 dark:text-gray-200">
                                  {m.label || m.id}
                                </code>
                                {isThisActive ? (
                                  <span className="text-emerald-600 font-bold text-[10px] flex items-center gap-0.5">
                                    <CheckCircle2 className="w-3 h-3" /> ATIVO
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => activate(p.id, m.id)}
                                    className="text-[10px] font-bold px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid={`ai-activate-${p.id}-${m.id}`}
                                  >
                                    Ativar
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {p.last_refresh && (
                        <p className="text-[10px] text-gray-400 mt-1">
                          Última atualização: {new Date(p.last_refresh).toLocaleString('pt-BR')}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-sm text-blue-900 dark:text-blue-200">
        <b>Como funciona:</b> assim que você salvar uma API key válida, o sistema consulta a API do
        provedor e mostra <b>todos os modelos disponíveis</b> na sua conta. Escolha o modelo desejado
        e clique em <b>Ativar</b>. O modelo ativo passa a ser a IA usada nas funcionalidades internas
        (OCR de notas, importação de estoque, chat interno etc).
      </div>
    </div>
  );
}
